import { useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Eyebrow,
  EmptyState,
  Input,
  MediumText,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusText,
  StrongText,
  Subheading,
  SurfaceCard,
  SurfaceRow,
  surfaceRowClassName,
} from '../../../components/ui'
import { QueueListIcon } from '@heroicons/react/20/solid'
import type { ComponentProps, FormEvent } from 'react'
import { INITIAL_GOALS, GOAL_ACTIONS } from '../constants'
import { uiClasses } from '../../shared/uiClasses'
import { GradientSphereMetricCard, sphereColorAt } from '../components/GradientSphereCard'

const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_MUTED = uiClasses.textMuted
const TEXT_SUBTLE = uiClasses.textSubtle
const TEXT_FAINT = uiClasses.textFaint
const BAR_TRACK = uiClasses.barTrack
const BAR_FILL_NEUTRAL = uiClasses.barFillNeutral
const CHECK_TOGGLE_IDLE = uiClasses.checkToggleIdle
type BadgeColor = ComponentProps<typeof Badge>['variant']

type GoalStatus = 'achieved' | 'in_progress' | 'planned'
type GoalDirection = 'higher' | 'lower'

interface Goal {
  id: string
  category: string
  current: number
  deadline: string
  direction: GoalDirection
  progress: number
  startDate: string
  status: GoalStatus
  target: number
  title: string
  unit: string
}

interface GoalAction {
  done: boolean
  goalId: string
  id: string
  text: string
}

const CATEGORY_OPTIONS = [
  'Cardiovascular',
  'Sleep',
  'Body Composition',
  'Nutrients',
  'Fitness',
  'Metabolic',
] as const

function statusColor(status: GoalStatus): BadgeColor {
  if (status === 'achieved') return 'green'
  if (status === 'in_progress') return 'blue'
  return 'zinc'
}

function statusLabel(status: GoalStatus): string {
  if (status === 'achieved') return 'Achieved'
  if (status === 'in_progress') return 'In Progress'
  return 'Planned'
}

function progressBarColor(status: GoalStatus): string {
  if (status === 'achieved') return 'dash-viz-score-high'
  if (status === 'in_progress') return 'dash-viz-score-mid'
  return BAR_FILL_NEUTRAL
}

function toGoalStatus(progress: number): GoalStatus {
  if (progress >= 100) {
    return 'achieved'
  }

  if (progress > 0) {
    return 'in_progress'
  }

  return 'planned'
}

function normalizeGoalDirection(value: string): GoalDirection {
  if (value === 'higher' || value === 'lower') {
    return value
  }

  throw new Error('[GOAL_DIRECTION_INVALID] Goal direction must be "higher" or "lower".')
}

function normalizeGoalStatus(value: string): GoalStatus {
  if (value === 'achieved' || value === 'in_progress' || value === 'planned') {
    return value
  }

  throw new Error('[GOAL_STATUS_INVALID] Goal status must be achieved, in_progress, or planned.')
}

function createEntityId(prefix: string): string {
  if (typeof globalThis.crypto?.randomUUID !== 'function') {
    throw new Error('[CRYPTO_UUID_UNAVAILABLE] Browser crypto.randomUUID() is required.')
  }

  return `${prefix}-${globalThis.crypto.randomUUID()}`
}

function todayLabel(): string {
  return new Date().toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function GoalsTab() {
  const [goals, setGoals] = useState<Goal[]>(() =>
    INITIAL_GOALS.map((goal) => ({
      ...goal,
      direction: normalizeGoalDirection(goal.direction),
      status: normalizeGoalStatus(goal.status),
    }))
  )
  const [goalActions, setGoalActions] = useState<GoalAction[]>(() => GOAL_ACTIONS.map((action) => ({ ...action })))
  const [newTitle, setNewTitle] = useState('')
  const [newCategory, setNewCategory] = useState<(typeof CATEGORY_OPTIONS)[number] | ''>('')
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [statusTone, setStatusTone] = useState<'danger' | 'success'>('success')

  const totalGoals = goals.length
  const achieved = goals.filter((goal) => goal.status === 'achieved').length
  const inProgress = goals.filter((goal) => goal.status === 'in_progress').length
  const avgProgress = goals.length
    ? Math.round(goals.reduce((sum, goal) => sum + goal.progress, 0) / goals.length)
    : 0

  const summaryStats = useMemo(
    () => [
      { label: 'Total Goals', value: totalGoals },
      { label: 'Achieved', value: achieved },
      { label: 'In Progress', value: inProgress },
      { label: 'Avg Progress', value: `${avgProgress}%` },
    ],
    [achieved, avgProgress, inProgress, totalGoals]
  )

  const isAddDisabled = !newTitle.trim() || !newCategory

  function refreshGoalProgress(goalId: string, nextActions: GoalAction[]) {
    const actionsForGoal = nextActions.filter((action) => action.goalId === goalId)
    if (actionsForGoal.length === 0) {
      return
    }

    const doneCount = actionsForGoal.filter((action) => action.done).length
    const nextProgress = Math.round((doneCount / actionsForGoal.length) * 100)

    setGoals((currentGoals) =>
      currentGoals.map((goal) =>
        goal.id === goalId
          ? {
              ...goal,
              current: Number(((goal.target * nextProgress) / 100).toFixed(1)),
              progress: nextProgress,
              status: toGoalStatus(nextProgress),
            }
          : goal
      )
    )
  }

  function handleToggleAction(actionId: string) {
    let touchedGoalId = ''

    setGoalActions((currentActions) => {
      const nextActions = currentActions.map((action) => {
        if (action.id !== actionId) {
          return action
        }

        touchedGoalId = action.goalId
        return {
          ...action,
          done: !action.done,
        }
      })

      if (touchedGoalId) {
        refreshGoalProgress(touchedGoalId, nextActions)
      }

      return nextActions
    })
  }

  function handleAddGoal(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setStatusMessage(null)

    const normalizedTitle = newTitle.trim()
    if (!normalizedTitle || !newCategory) {
      setStatusTone('danger')
      setStatusMessage('Goal title and category are required.')
      return
    }

    const duplicate = goals.some(
      (goal) =>
        goal.title.trim().toLowerCase() === normalizedTitle.toLowerCase() &&
        goal.category === newCategory
    )
    if (duplicate) {
      setStatusTone('danger')
      setStatusMessage('A goal with this title and category already exists.')
      return
    }

    try {
      const nextGoal: Goal = {
        id: createEntityId('goal'),
        title: normalizedTitle,
        category: newCategory,
        target: 1,
        current: 0,
        unit: 'milestone',
        direction: 'higher',
        startDate: todayLabel(),
        deadline: 'TBD',
        status: 'planned',
        progress: 0,
      }

      setGoals((currentGoals) => [nextGoal, ...currentGoals])
      setNewTitle('')
      setNewCategory('')
      setStatusTone('success')
      setStatusMessage(`Added goal "${normalizedTitle}".`)
    } catch (error) {
      setStatusTone('danger')
      setStatusMessage(error instanceof Error ? error.message : 'Unable to add goal.')
    }
  }

  return (
    <div className="space-y-6">
      <SurfaceCard as="article" className="goals-header-shell">
        <div className="goals-header-layout">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Subheading variant="dashboard">Goals cockpit</Subheading>
              <Badge variant="zinc">
                {achieved} complete
              </Badge>
            </div>

            <div className="goals-inline-metrics mt-5">
              {summaryStats.map((stat, index) => (
                <GradientSphereMetricCard
                  key={stat.label}
                  label={stat.label}
                  value={stat.value}
                  sphereColor={sphereColorAt(index)}
                />
              ))}
            </div>
          </div>

          <SurfaceRow as="form" onSubmit={handleAddGoal} className="goals-quick-add">
            <Eyebrow variant="section" tone="subtle">Add new goal</Eyebrow>
            <div className="mt-3 space-y-3">
              <Input
                type="text"
                placeholder="Goal title"
                value={newTitle}
                onChange={(event) => setNewTitle(event.target.value)}
              />
              <Select
                value={newCategory || undefined}
                onValueChange={(nextCategory) =>
                  setNewCategory(nextCategory as (typeof CATEGORY_OPTIONS)[number] | '')
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button disabled={isAddDisabled}>
                Add goal
              </Button>
            </div>
            {statusMessage && (
              <StatusText className="mt-3" tone={statusTone === 'danger' ? 'danger' : 'success'} aria-live="polite">
                {statusMessage}
              </StatusText>
            )}
          </SurfaceRow>
        </div>
      </SurfaceCard>

      <SurfaceCard as="article">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Subheading variant="dashboard">Goal stream</Subheading>
          <Badge variant="zinc">{totalGoals} goals</Badge>
        </div>

        <div className="goals-stream mt-4">
          {goals.map((goal) => {
            const actions = goalActions.filter((action) => action.goalId === goal.id)
            const completedActions = actions.filter((action) => action.done).length

            return (
              <section key={goal.id} className="goals-stream-item">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <StrongText as="h3" size="base">{goal.title}</StrongText>
                    <p className={`mt-1 text-sm/6 ${TEXT_SUBTLE}`}>
                      {goal.current} / {goal.target} {goal.unit}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Badge variant="zinc">{goal.category}</Badge>
                      <Badge variant={statusColor(goal.status)}>
                        {statusLabel(goal.status)}
                      </Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <Eyebrow variant="mini" tone="subtle">Deadline</Eyebrow>
                    <MediumText className="mt-1" tone="secondary">{goal.deadline}</MediumText>
                  </div>
                </div>

                <div className="mt-4">
                  <div className={`flex items-center justify-between text-sm/6 ${TEXT_MUTED}`}>
                    <span>Progress</span>
                    <span>{goal.progress}%</span>
                  </div>
                  <div className={`mt-2 h-2.5 w-full rounded-full ${BAR_TRACK}`}>
                    <div
                      className={`h-2.5 rounded-full transition-all ${progressBarColor(goal.status)}`}
                      style={{ width: `${goal.progress}%` }}
                    />
                  </div>
                </div>

                {actions.length > 0 ? (
                  <>
                    <div className="goals-actions-grid mt-4">
                      {actions.map((action) => (
                        <Button
                          key={action.id}
                          type="button"
                          variant="unstyled"
                          className={`${surfaceRowClassName} flex w-full items-center gap-3 text-left`}
                          onClick={() => handleToggleAction(action.id)}
                          aria-pressed={action.done}
                        >
                          <span
                            className={`flex size-5 shrink-0 items-center justify-center rounded border ${
                              action.done
                                ? 'dashboard-check-toggle-active'
                                : CHECK_TOGGLE_IDLE
                            }`}
                          >
                            {action.done && (
                              <svg
                                className="size-3"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={3}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                          </span>
                          <span
                            className={`text-sm/6 ${action.done ? `${TEXT_FAINT} line-through` : TEXT_SECONDARY}`}
                          >
                            {action.text}
                          </span>
                        </Button>
                      ))}
                    </div>
                    <p className={`mt-2 text-xs/5 ${TEXT_SUBTLE}`}>
                      {completedActions}/{actions.length} actions complete
                    </p>
                  </>
                ) : (
                  <EmptyState
                    className={`${surfaceRowClassName} mt-4 !py-4`}
                    icon={QueueListIcon}
                    title="No action checklist yet"
                    description="Add actions to break this goal into clear next steps."
                    titleClassName={TEXT_SECONDARY}
                    descriptionClassName={TEXT_SUBTLE}
                    iconClassName={TEXT_FAINT}
                  />
                )}
              </section>
            )
          })}
        </div>
      </SurfaceCard>
    </div>
  )
}
